import { z } from 'zod';
import { emailSchema } from './common.js';

export const PASSWORD_MIN_LENGTH = 12;
export const PASSWORD_MAX_LENGTH = 128;

/**
 * NIST-flavoured policy: length does the heavy lifting, with a light
 * composition rule to stop `aaaaaaaaaaaa`. The max length exists because
 * Argon2 hashes the whole input — unbounded passwords are a DoS vector.
 */
export const passwordSchema = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters`)
  .max(PASSWORD_MAX_LENGTH)
  .refine((value) => /[a-z]/u.test(value), 'Include a lowercase letter')
  .refine((value) => /[A-Z]/u.test(value), 'Include an uppercase letter')
  .refine((value) => /\d/u.test(value), 'Include a number');

export const personNameSchema = z.string().trim().min(1, 'Required').max(80);

/** Self-service signup: provisions an organization and its first Admin. */
export const registerSchema = z.object({
  organizationName: z.string().trim().min(2, 'Required').max(120),
  firstName: personNameSchema,
  lastName: personNameSchema,
  email: emailSchema,
  password: passwordSchema,
});
export type RegisterInput = z.infer<typeof registerSchema>;

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Required').max(PASSWORD_MAX_LENGTH),
});
export type LoginInput = z.infer<typeof loginSchema>;

export const changePasswordSchema = z
  .object({
    currentPassword: z.string().min(1, 'Required').max(PASSWORD_MAX_LENGTH),
    newPassword: passwordSchema,
  })
  .refine((value) => value.currentPassword !== value.newPassword, {
    message: 'Choose a password you have not used before',
    path: ['newPassword'],
  });
export type ChangePasswordInput = z.infer<typeof changePasswordSchema>;

export const acceptInviteSchema = z.object({
  token: z.string().min(16).max(256),
  password: passwordSchema,
});
export type AcceptInviteInput = z.infer<typeof acceptInviteSchema>;
